import React, { useEffect } from "react";
import Loader from "../components/common/loader/loader";
import { TableColumn } from "../interfaces/tables";
import Table from "../components/ui/table";
import { MdDelete, MdEdit } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES } from "../constants/routes";
import { Button } from "../components/ui/button";
import Error from "../components/common/error/error";
import TableHeaders from "../components/ui/table-headers";
import { siteSettings } from "../settings/site-settings";
import { deleteCategory, listAllCategories } from "../api/admin/categories";
import { cn, notify } from "../lib/utils";
import { PlusIcon } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import DeleteCategoryDialog from "../components/categories/delete-category-dialog";
import usePageTitle from "../hooks/usePageTitle";

function Categories() {
  usePageTitle("Categories");

  const navigate = useNavigate();
  const [categories, setCategories] = React.useState<any>([]);
  const [tableData, setTableData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  // table headers state, search, sort, page.
  const [sort, setSort] = React.useState<any>({ createdAt: -1 });
  const [search, setSearch] = React.useState(null);

  const [page, setPage] = React.useState(1);

  const [pageSize, setPageSize] = React.useState(
    siteSettings.tables.categories.defaultPageSize.key
  );

  const [paginationTotalCount, setPaginationTotalCount] = React.useState(0);

  const [selectedCategoryForDelete, setSelectedCategoryForDelete] =
    React.useState<any | null>(null);

  const [categoryDeleteDialogOpen, setCategoryDeleteDialogOpen] =
    React.useState(false);

  const handleOpenDeleteCategoryDialog = (category: any) => {
    setSelectedCategoryForDelete(category);
    setCategoryDeleteDialogOpen(true);
  };

  const handleCloseDeleteCategoryDialog = () => {
    setSelectedCategoryForDelete(null);
    setCategoryDeleteDialogOpen(false);
  };

  const fetchCategories = async () => {
    const response = await listAllCategories({
      page,
      sort,
      search,
      pageSize,
    });

    if (!response || response.status !== 200) {
      console.log("Error fetching users");
      setError(true);
      setLoading(false);

      return;
    }

    setCategories(response.data?.data);
    setTableData(formatDataForTable(response.data?.data));
    setPaginationTotalCount(response.data?.total);
    setLoading(false);
  };

  const handleOnSearchChange = (searchObj: any) => {
    if (!searchObj || !searchObj.value || searchObj.value === "") {
      setSearch(null);
      return;
    }

    setSearch(searchObj);
    setPage(1); // reset page to 1 when search changes
  };

  const handleOnSortChange = (sortObj: any) => {
    if (!sortObj) {
      setSort(null);
      return;
    }

    setSort(sortObj);
  };

  const handleCategoryDelete = async (categoryId: string) => {
    const response = await deleteCategory(categoryId);

    if (!response || response.status !== 200) {
      notify({
        type: "error",
        message: "Error deleting category",
      });
      return;
    }

    notify({
      type: "success",
      message: "Category deleted successfully",
    });

    setCategoryDeleteDialogOpen(false);
    fetchCategories();
  };

  const handleOnPageSizeChange = (pageSize: number) => {
    setPageSize(pageSize);
    setPage(1);
  };

  // headers change listener
  useEffect(() => {
    fetchCategories();
  }, [page, sort, search, pageSize]);

  const columns: TableColumn[] = [
    {
      key: "image",
      label: "Image",
      render: (cell, row) => {
        if (cell) {
          return (
            <img
              src={cell}
              alt="category"
              className="h-[50px] w-[35px] object-cover rounded-md"
            />
          );
        } else {
          return (
            <div className="w-[35px] h-[50px] bg-borderColor rounded-md flex items-center justify-center text-gray-500">
              N/A
            </div>
          );
        }
      },
    },

    { key: "_id", label: "Internal Id" },

    { key: "internalName", label: "Internal Name" },
    { key: "displayName", label: "Display Name (EN)" },
    {
      key: "subcategories",
      label: "Subcategories",
      render: (cell, row) => (
        <div className="w-full flex items-center /justify-between ">
          <span className="font-bold">({cell?.length || 0})</span>

          {cell &&
            cell.length > 0 &&
            cell.slice(0, 2).map((subcategory: any, index: number) => (
              <div
                key={index}
                className={cn("text-sm w-max h-full ", {
                  "ml-1": index < 2,
                })}
              >
                {subcategory.internalName}
                {index < 1 && cell.length > 1 && ","}
              </div>
            ))}

          {cell && cell.length > 2 && (
            <div className="ml-1 flex">
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <PlusIcon size={16} className="text-primary" />
                  </Tooltip.Trigger>

                  <Tooltip.Portal>
                    <Tooltip.Content
                      className="data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none rounded-[4px] bg-black text-white px-[15px] py-[10px] text-[15px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity]"
                      sideOffset={5}
                    >
                      <div className="flex flex-col gap-y-2">
                        {cell.map((sub: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-x-2"
                          >
                            - <span>{sub.internalName}</span>
                          </div>
                        ))}
                      </div>
                      <Tooltip.Arrow />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          )}
        </div>
      ),
    },
    { key: "slug", label: "Slug" },

    { key: "description", label: "Description (EN)" },

    {
      key: "createdAt",
      label: "Created At",
      render: (cell) => {
        return cell ? new Date(cell).toLocaleDateString() : "N/A";
      },
    },

    {
      key: "btn",
      label: "Actions",
      fixed: true,
      render: (cell, row) => {
        return (
          <div className="flex gap-x-4">
            <Link to={`${ROUTES.CATEGORY_FORM}/${row._id}`}>
              <MdEdit
                title="Edit"
                className="hover:text-primary cursor-pointer"
                size={24}
              />
            </Link>

            <MdDelete
              title="Delete"
              className="hover:text-red-600 cursor-pointer"
              size={24}
              onClick={() => {
                handleOpenDeleteCategoryDialog(row);
              }}
            />
          </div>
        );
      },
    },
  ];

  const formatDataForTable = (data: any) => {
    return data.map((d: any) => {
      return {
        _id: d._id,
        internalName: d.internalName,
        displayName: d.displayName?.en,
        slug: d.slug,
        description: d.description?.en || "N/A",
        subcategories: d.subcategories,
        createdAt: d.createdAt,
        image: d.image,
        btn: d._id,
        fullDoc: d,
      };
    });
  };

  return (
    <div className="w-full h-full">
      {loading && <Loader pageLoader />}

      {!loading && error && <Error />}

      {!loading && !error && (
        <div className="w-[95%] mx-auto ">
          <div className="w-full mx-auto">
            <h1 className="text-2xl font-bold text-primary">Categories</h1>
            {paginationTotalCount && paginationTotalCount > 0 && (
              <p className="text-bodyDark">Total: {paginationTotalCount}</p>
            )}
          </div>

          <div className="w-full mx-auto flex items-end justify-end">
            <Button
              onClick={() => {
                navigate(ROUTES.CATEGORY_FORM);
              }}
            >
              Create Category
            </Button>
          </div>

          <div className="w-full block  mx-auto my-10 pb-20">
            <TableHeaders
              searchByOptions={siteSettings.tables.categories.searchByOptions}
              onSearchChange={handleOnSearchChange}
              sortByOptions={siteSettings.tables.categories.sortByOptions}
              onSortChange={handleOnSortChange}
              page={page}
              onPageChange={setPage}
              lastPage={
                paginationTotalCount === 0
                  ? 1
                  : Math.ceil(paginationTotalCount / pageSize)
              }
              pageSizeOptions={siteSettings.tables.categories.pageSizeOptions}
              onPageSizeChange={handleOnPageSizeChange}
              defaultPageSize={siteSettings.tables.categories.defaultPageSize}
            />
            <Table columns={columns} data={tableData} />
          </div>

          <DeleteCategoryDialog
            category={selectedCategoryForDelete}
            open={categoryDeleteDialogOpen}
            onClose={handleCloseDeleteCategoryDialog}
            onCategoryDelete={(categoryId: string) => {
              handleCategoryDelete(categoryId);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default Categories;
